import { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import TrustedBy from "./TrustedBy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * PageShell — wraps a public page with header, footer and an optional hero band.
 *
 * Hero content can be overridden from the admin: pass `pageKey="about"` (etc.)
 * and the shell will fetch `/api/public/pages/hero.<pageKey>` and apply any
 * non-empty fields from the saved value: { eyebrow, title, subtitle, banner_url }.
 * The static props passed in remain the default fallback.
 */
export default function PageShell({ eyebrow, title, subtitle, children, banner, hideTrustedBy, pageKey }) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    if (!pageKey) return;
    axios.get(`${API}/public/pages/hero.${pageKey}`)
      .then((r) => {
        const v = r.data?.value;
        if (v && (v.eyebrow || v.title || v.subtitle || v.banner_url)) setOverride(v);
      })
      .catch(() => {});
  }, [pageKey]);

  const finalEyebrow  = override?.eyebrow  || eyebrow;
  const finalTitle    = override?.title    || title;
  const finalSubtitle = override?.subtitle || subtitle;
  const rawBanner     = override?.banner_url || banner;
  const finalBanner   = rawBanner ? (rawBanner.startsWith("http") ? rawBanner : `${BACKEND}${rawBanner}`) : null;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="public-page">
      <Header />
      <main>
        {(finalTitle || finalBanner) && (
          <section
            className="relative overflow-hidden text-white"
            data-testid="page-hero"
            style={{
              background: finalBanner
                ? `linear-gradient(180deg, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.65) 100%), url(${finalBanner}) center/cover no-repeat`
                : "linear-gradient(120deg, #0F172A 0%, #1E293B 55%, #0369A1 100%)",
            }}
          >
            <div className="absolute inset-0 opacity-[0.07]" aria-hidden style={{
              backgroundImage: "radial-gradient(circle at 25% 25%, white 1px, transparent 1.5px), radial-gradient(circle at 75% 75%, white 1px, transparent 1.5px)",
              backgroundSize: "44px 44px",
            }} />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
              {finalEyebrow && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-[10px] tracking-[0.3em] uppercase font-semibold mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FB923C]" /> {finalEyebrow}
                </div>
              )}
              <h1
                className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl text-white"
                style={{ animation: "blurUp 0.8s ease-out both" }}
              >
                {finalTitle}
              </h1>
              {finalSubtitle && (
                <p
                  className="mt-5 text-base sm:text-lg text-white/80 max-w-2xl"
                  style={{ animation: "blurUp 0.9s ease-out 0.15s both" }}
                >
                  {finalSubtitle}
                </p>
              )}
            </div>
          </section>
        )}
        {children}
      </main>
      {!hideTrustedBy && <TrustedBy />}
      <Footer />
    </div>
  );
}
